// What a route shows while its loader is still running.
//
// The router renders this after `defaultPendingMs` (router.ts) in place of the
// outgoing screen, and keeps it for at least `defaultPendingMinMs` so it never
// flashes. Before it existed a click on the sidebar produced nothing for the
// whole loader wait — up to several seconds while `POST /campaigns/overview`
// held the serial D1 connection (2026-09-13) — and read as a dead click.
//
// It is a shape, not a screen: a header block where the title and primary
// action go, and a panel of table rows. No data, no hooks, no router, no
// network — it MUST NOT import anything that reads a resource, because the
// whole point is that it renders while the resources are still loading. The
// dom test pins that by scanning this file's imports.
//
// Accessibility: the region is `role="status"`, so assistive technology hears
// one "Loading" and nothing else — the bones themselves are `aria-hidden`.
// `aria-busy` is deliberately NOT on the status element: AT may defer
// announcing a busy live region until busy clears, and here it never clears
// before unmount, so the one announcement would be the one thing lost. The
// busy marker sits on the hidden body instead. The pulse is CSS
// (`.route-pending-bone` in styles.css) and stops under `prefers-reduced-motion`.

/** Table-row placeholders under the header block: enough to read as a list. */
const ROW_COUNT = 7;

/** Bones per row; widths and the phone's two-column cut live in CSS. */
const CELLS_PER_ROW = 5;

function Row({ head = false }: { head?: boolean }) {
  return (
    <div className={head ? 'route-pending-row route-pending-row--head' : 'route-pending-row'}>
      {Array.from({ length: CELLS_PER_ROW }, (_, i) => (
        <span key={i} className="route-pending-bone" />
      ))}
    </div>
  );
}

export function RoutePending() {
  return (
    <div className="route-pending" role="status">
      <span className="sr-only">Loading…</span>
      <div className="route-pending-hd" aria-hidden="true">
        <span className="route-pending-bone route-pending-title" />
        <span className="route-pending-bone route-pending-action" />
      </div>
      <div className="route-pending-body" aria-hidden="true" aria-busy="true">
        <div className="panel route-pending-panel">
          <Row head />
          {Array.from({ length: ROW_COUNT }, (_, i) => (
            <Row key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
